import { useLocation, Link } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

import { ReactComponent as HomeIcon } from "../../../assets/icons/logo.svg";
import { ReactComponent as FolderIcon } from "../../../assets/icons/folder.svg";
import { ReactComponent as SupportIcon } from "../../../assets/icons/supporter.svg";
import { ReactComponent as UserIcon } from "../../../assets/icons/user.svg";
import { ReactComponent as HomeActiveIcon } from "../../../assets/icons/logo-active.svg";
import { ReactComponent as FolderActiveIcon } from "../../../assets/icons/folder-active.svg";
import { ReactComponent as SupportActiveIcon } from "../../../assets/icons/supporter-active.svg";
import { ReactComponent as UserActiveIcon } from "../../../assets/icons/user-active.svg";
import { createPortal } from "react-dom";

const BottomNavbar = () => {
  const location = useLocation();
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const shouldReduceMotion = useReducedMotion();

  const navItems = useMemo(
    () => [
      {
        id: "/trang-chu",
        label: "Trang chủ",
        icon: <HomeIcon className="w-6 h-6" />,
        activeIcon: <HomeActiveIcon className="w-6 h-6" />,
        type: "internal",
      },
      {
        id: "/quan-ly-ho-so",
        label: "Quản lý hồ sơ",
        icon: <FolderIcon className="w-6 h-6" />,
        activeIcon: <FolderActiveIcon className="w-6 h-6" />,
        type: "internal",
      },
      {
        id: "support",
        label: "Tư vấn nhanh",
        icon: <SupportIcon className="w-6 h-6" />,
        activeIcon: <SupportActiveIcon className="w-6 h-6" />,
        type: "support",
      },
      {
        id: "/tai-khoan",
        label: "Tài khoản",
        icon: <UserIcon className="w-6 h-6" />,
        activeIcon: <UserActiveIcon className="w-6 h-6" />,
        type: "internal",
      },
    ],
    []
  );

  // Đóng popover khi click ra ngoài
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!dropdownRef.current || !buttonRef.current) return;
      if (
        !dropdownRef.current.contains(e.target) &&
        !buttonRef.current.contains(e.target)
      ) {
        setIsSupportOpen(false);
      }
    };

    if (isSupportOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSupportOpen]);

  // Đóng popover bằng ESC
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && isSupportOpen) {
        setIsSupportOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isSupportOpen]);

  // Kiểm tra active (section-aware) - CHỈ cho internal links
  const isRouteActive = (id) => {
    if (id === "/trang-chu") {
      return (
        location.pathname === "/" || location.pathname.startsWith("/trang-chu")
      );
    }
    return location.pathname === id || location.pathname.startsWith(id + "/");
  };

  // Variants
  const btnTap = shouldReduceMotion ? {} : { scale: 0.98 };
  const popoverV = {
    hidden: { opacity: 0, y: 8, scale: 0.95 },
    show: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.2, ease: [0.16, 1, 0.3, 1] },
    },
    exit: {
      opacity: 0,
      y: 4,
      scale: 0.95,
      transition: { duration: 0.15 },
    },
  };

  return (
    <div
      className="
    fixed bottom-0 left-0 right-0 z-50
    h-[88px] px-4 py-1
    rounded-t-3xl
    bg-gray/10 backdrop-blur-sm
    border-t border-white/40
    shadow-[0_-8px_24px_rgba(0,0,0,0.08)]
  "
    >
      <nav className="flex justify-around" aria-label="Điều hướng đáy">
        {navItems.map((item) => {
          // Support tab CHỈ active khi popover mở
          // Internal tabs active dựa trên route
          const isActive =
            item.type === "support" ? isSupportOpen : isRouteActive(item.id);

          // Nút hỗ trợ (popover)
          if (item.type === "support") {
            return (
              <div
                key={item.id}
                className="relative flex flex-col items-center justify-center py-2"
              >
                <button
                  ref={buttonRef}
                  onClick={() => setIsSupportOpen((p) => !p)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setIsSupportOpen((p) => !p);
                    }
                  }}
                  aria-expanded={isSupportOpen}
                  aria-controls="support-popover"
                  aria-haspopup="true"
                  className="flex flex-col items-center focus:outline-none"
                >
                  <motion.div
                    whileTap={btnTap}
                    className={`relative flex h-9 w-16 items-center justify-center rounded-3xl transition-colors ${
                      isActive ? " text-blue-600" : "text-gray-600"
                    }`}
                  >
                    {isActive ? item.activeIcon : item.icon}
                  </motion.div>
                  <span
                    className={`mt-1 whitespace-nowrap text-sm ${
                      isActive
                        ? "text-blue-600 font-sf-medium"
                        : "text-gray-700"
                    }`}
                  >
                    {item.label}
                  </span>
                </button>

                {createPortal(
                  <AnimatePresence>
                    {isSupportOpen && (
                      <>
                        {/* Backdrop */}
                        <motion.div
                          className="fixed inset-0 z-[1000] bg-black/40 backdrop-blur-sm"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          onClick={() => setIsSupportOpen(false)}
                        />

                        {/* Bottom Sheet */}
                        <motion.div
                          className="fixed inset-x-0 bottom-0 z-[1001] rounded-t-[28px] bg-white shadow-2xl"
                          initial={{ y: "100%" }}
                          animate={{ y: 0 }}
                          exit={{ y: "100%" }}
                          transition={{
                            type: "spring",
                            stiffness: 420,
                            damping: 40,
                          }}
                          drag="y"
                          dragConstraints={{ top: 0, bottom: 0 }}
                          dragElastic={{ bottom: 0.6 }}
                          onDragEnd={(_, info) => {
                            if (info.offset.y > 120 || info.velocity.y > 700) {
                              setIsSupportOpen(false);
                            }
                          }}
                        >
                          {/* Handle */}
                          <div className="flex justify-center pt-3">
                            <div className="h-1 w-10 rounded-full bg-gray-300" />
                          </div>

                          {/* Header */}
                          <div className="px-5 pt-4 pb-2">
                            <h3 className="text-lg font-sf-semibold text-gray-900">
                              Liên hệ tư vấn
                            </h3>
                            <p className="mt-0.5 text-sm text-gray-500">
                              Chúng tôi luôn sẵn sàng hỗ trợ bạn
                            </p>
                          </div>

                          {/* Actions */}
                          <div className="px-4 pb-6 pt-2 space-y-3">
                            {/* Zalo */}
                            <a
                              href="https://zalo.me/0819009697"
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => setIsSupportOpen(false)}
                              className="flex items-center gap-4 rounded-2xl border border-gray-100 px-4 py-4 hover:bg-gray-50 active:bg-gray-100 transition"
                            >
                              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-50">
                                <span className="text-blue-600 font-bold">
                                  Z
                                </span>
                              </div>
                              <div className="flex flex-col">
                                <span className="text-[15px] font-sf-medium text-gray-900">
                                  Tư vấn qua Zalo
                                </span>
                                <span className="text-sm text-gray-500">
                                  Chat trực tiếp với chúng tôi
                                </span>
                              </div>
                            </a>

                            {/* Phone */}
                            <a
                              href="tel:0819009697"
                              onClick={() => setIsSupportOpen(false)}
                              className="flex items-center gap-4 rounded-2xl border border-gray-100 px-4 py-4 hover:bg-gray-50 active:bg-gray-100 transition"
                            >
                              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-green-50">
                                📞
                              </div>
                              <div className="flex flex-col">
                                <span className="text-[15px] font-sf-medium text-gray-900">
                                  Gọi điện thoại
                                </span>
                                <span className="text-sm text-gray-500">
                                  0819 009 697
                                </span>
                              </div>
                            </a>
                          </div>
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>,
                  document.body
                )}
              </div>
            );
          }

          // Link nội bộ (mặc định)
          return (
            <Link
              key={item.id}
              to={item.id}
              className="flex flex-col items-center justify-center py-2"
              aria-current={isActive ? "page" : undefined}
            >
              <motion.div
                whileTap={btnTap}
                className={`relative flex h-9 w-16 items-center justify-center rounded-3xl transition-colors ${
                  isActive ? " text-blue-600" : "text-gray-600"
                }`}
              >
                {isActive ? item.activeIcon : item.icon}
              </motion.div>
              <span
                className={`mt-1 whitespace-nowrap text-sm ${
                  isActive ? "text-blue-600 font-sf-medium" : "text-gray-700"
                }`}
              >
                {item.label}
              </span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default BottomNavbar;
